import type { JSX, ReactNode } from 'react';
import { useI18n } from '../lib/i18n.js';
import { ApiError } from '../lib/api.js';

export function Card({ children, title }: { children: ReactNode; title?: string }): JSX.Element {
  if (!title) return <div className="card">{children}</div>;
  return (
    <section className="card-group">
      <h2 className="section-title">{title}</h2>
      <div className="card">{children}</div>
    </section>
  );
}

/**
 * Two or three mutually exclusive views.
 *
 * Distinct from `Filter`, which is a set of independent on/off narrowings —
 * conflating the two is what made every pill in this app look identical.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div className="segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A pressable narrowing. Carries its own on/off state for assistive tech. */
export function Filter({
  label,
  active,
  onClick,
}: {
  label: ReactNode;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" className="filter" aria-pressed={active} onClick={onClick}>
      {label}
    </button>
  );
}

export function Spinner(): JSX.Element {
  const { t } = useI18n();
  return <div className="spinner">{t('common.loading')}</div>;
}

export function Empty({ text }: { text?: string }): JSX.Element {
  const { t } = useI18n();
  return <div className="empty">{text ?? t('common.nothing_found')}</div>;
}

export function ErrorBox({ error }: { error: unknown }): JSX.Element {
  const { t, has } = useI18n();
  const apiError = error instanceof ApiError ? error : null;
  const key = apiError ? `error.${apiError.code}` : 'error.generic';
  const message = has(key)
    ? t(key, { detail: apiError?.detail ?? '' })
    : ((error as Error)?.message ?? t('error.generic'));

  return (
    <div className="error">
      <div>{message}</div>
      {apiError?.detail && apiError.code !== 'JiraValidationError' ? (
        <div className="small mono" style={{ marginTop: 6, opacity: 0.75 }}>
          {apiError.detail}
        </div>
      ) : null}
    </div>
  );
}

export function StatusChip({
  name,
  category,
}: {
  name: string;
  category?: string;
}): JSX.Element {
  const tone =
    category === 'done' ? 'chip-done' : category === 'indeterminate' ? 'chip-indeterminate' : 'chip-new';
  return <span className={`chip ${tone}`}>{name}</span>;
}

export function Ltr({ children }: { children: ReactNode }): JSX.Element {
  return <span className="ltr">{children}</span>;
}

export function Toggle({
  label,
  checked,
  disabled,
  hint,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  hint?: string;
  onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <div className="row">
      <div className="row-main">
        <div className="row-title">{label}</div>
        {hint ? <div className="row-sub">{hint}</div> : null}
      </div>
      <button
        type="button"
        className="switch"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
    </div>
  );
}

export function Row({
  title,
  subtitle,
  right,
  onClick,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
}): JSX.Element {
  return (
    <div
      className="row"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') onClick();
            }
          : undefined
      }
    >
      <div className="row-main">
        <div className="row-title">{title}</div>
        {subtitle ? <div className="row-sub">{subtitle}</div> : null}
      </div>
      {right}
    </div>
  );
}
